import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { changeMyPassword, fetchMyProfile, updateMyName } from '../api/userApi';
import { useAuth } from '../context/AuthContext';

function ProfilePage() {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [nameForm, setNameForm] = useState({ name: '' });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  });
  const [loading, setLoading] = useState(true);
  const [nameSubmitting, setNameSubmitting] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [nameMessage, setNameMessage] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await fetchMyProfile();
        setProfile(result);
        setNameForm({ name: result.name ?? '' });
      } catch (loadError) {
        setError(loadError.message || '내 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleNameChange = (event) => {
    setNameForm({ name: event.target.value });
    setNameMessage('');
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    setPasswordMessage('');
  };

  const handleNameSubmit = async (event) => {
    event.preventDefault();
    setNameSubmitting(true);
    setNameMessage('');
    setError('');

    try {
      const updated = await updateMyName(nameForm.name.trim());
      setProfile(updated);
      await refreshUser();
      setNameMessage('이름이 수정되었습니다.');
    } catch (submitError) {
      setError(submitError.message || '이름 수정에 실패했습니다.');
    } finally {
      setNameSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (passwordForm.newPassword !== passwordForm.newPasswordConfirm) {
      setPasswordMessage('새 비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    setPasswordSubmitting(true);
    setPasswordMessage('');
    setError('');

    try {
      await changeMyPassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        newPasswordConfirm: '',
      });
      setPasswordMessage('비밀번호가 변경되었습니다. 다시 로그인해 주세요.');
      await logout();
      navigate('/login', { replace: true });
    } catch (submitError) {
      setPasswordMessage(submitError.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  if (loading) {
    return <div className="page">내 정보를 불러오는 중...</div>;
  }

  return (
    <section className="page">
      <div className="page-header">
        <h2>내 정보</h2>
      </div>

      {error ? <p className="login-error">{error}</p> : null}

      <div className="profile-sections">
        <section className="form-section profile-section">
          <h3>기본 정보</h3>
          <div className="profile-section-body">
            <div className="form-grid profile-form-grid">
              <label className="field">
                <span className="field-label">아이디</span>
                <input value={profile?.username || user?.username || ''} disabled />
              </label>
              <label className="field">
                <span className="field-label">권한</span>
                <input value={profile?.role || user?.role || ''} disabled />
              </label>
            </div>
            <form className="profile-section-form" onSubmit={handleNameSubmit}>
              <div className="form-grid profile-form-grid">
                <label className="field field-wide" htmlFor="name">
                  <span className="field-label">이름</span>
                  <input
                    id="name"
                    name="name"
                    value={nameForm.name}
                    onChange={handleNameChange}
                    required
                  />
                </label>
              </div>
              {nameMessage ? <p className="form-success">{nameMessage}</p> : null}
              <div className="button-row profile-section-actions">
                <button type="submit" disabled={nameSubmitting}>
                  {nameSubmitting ? '저장 중...' : '이름 저장'}
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="form-section profile-section">
          <h3>비밀번호 변경</h3>
          <form className="profile-section-body profile-section-form" onSubmit={handlePasswordSubmit}>
            <div className="form-grid profile-form-grid">
              <label className="field field-wide" htmlFor="currentPassword">
                <span className="field-label">현재 비밀번호</span>
                <input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  autoComplete="current-password"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </label>
              <label className="field" htmlFor="newPassword">
                <span className="field-label">새 비밀번호</span>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </label>
              <label className="field" htmlFor="newPasswordConfirm">
                <span className="field-label">새 비밀번호 확인</span>
                <input
                  id="newPasswordConfirm"
                  name="newPasswordConfirm"
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.newPasswordConfirm}
                  onChange={handlePasswordChange}
                  required
                />
              </label>
            </div>
            {passwordMessage ? <p className="login-error">{passwordMessage}</p> : null}
            <div className="button-row profile-section-actions">
              <button type="submit" disabled={passwordSubmitting}>
                {passwordSubmitting ? '변경 중...' : '비밀번호 변경'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </section>
  );
}

export default ProfilePage;
